import {
  DestroyRef,
  Directive,
  ElementRef,
  Renderer2,
  RendererStyleFlags2,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

const DRAG_THRESHOLD_PX = 10;
const SYNTHETIC_MOUSE_IGNORE_MS = 400;

@Directive({
  selector: '[bottomSheetGesture]',
  standalone: true,
})
export class BottomSheetGestureDirective {
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  enabled = input<boolean>(false, { alias: 'bottomSheetGesture' });
  dismissThreshold = input<number>(100);
  dialogElement = input<HTMLElement | null>(null);
  targetElement = input<HTMLElement | null>(null);
  dismiss = output<void>();

  private pointerStartY = 0;
  private pointerCurrentY = 0;
  private isDragging = signal(false);
  private canDragFromHandle = false;
  private lastPointerType: 'touch' | 'mouse' | null = null;
  private lastTouchEndTime = 0;
  private readonly handleSelector = '[data-gesture-handle]';

  private touchStartListener?: () => void;
  private touchMoveListener?: () => void;
  private touchEndListener?: () => void;
  private mouseDownListener?: () => void;
  private mouseMoveListener?: () => void;
  private mouseUpListener?: () => void;

  constructor() {
    effect(() => {
      if (this.enabled()) {
        this.setupGestures();
      } else {
        this.cleanupGestures();
      }
    });

    this.destroyRef.onDestroy(() => {
      this.cleanupGestures();
    });

    effect(() => {
      const dragging = this.isDragging();
      const host = this.elementRef.nativeElement as HTMLElement;
      if (dragging) {
        this.renderer.addClass(host, 'bottom-sheet-dragging');
      } else {
        this.renderer.removeClass(host, 'bottom-sheet-dragging');
      }
    });
  }

  private setupGestures(): void {
    const element = this.elementRef.nativeElement;

    this.touchStartListener = this.renderer.listen(element, 'touchstart', (e: TouchEvent) => this.handleTouchStart(e));
    this.touchMoveListener = this.renderer.listen(element, 'touchmove', (e: TouchEvent) => this.handleTouchMove(e));
    this.touchEndListener = this.renderer.listen(element, 'touchend', () => this.handleTouchEnd());

    this.mouseDownListener = this.renderer.listen(element, 'mousedown', (e: MouseEvent) => this.handleMouseDown(e));
  }

  private cleanupGestures(): void {
    this.removeTouchListeners();
    this.removeMouseDownListener();
    this.removeDocumentMouseListeners();
  }

  private removeTouchListeners(): void {
    if (this.touchStartListener) {
      this.touchStartListener();
      this.touchStartListener = undefined;
    }
    if (this.touchMoveListener) {
      this.touchMoveListener();
      this.touchMoveListener = undefined;
    }
    if (this.touchEndListener) {
      this.touchEndListener();
      this.touchEndListener = undefined;
    }
  }

  private removeMouseDownListener(): void {
    if (this.mouseDownListener) {
      this.mouseDownListener();
      this.mouseDownListener = undefined;
    }
  }

  private removeDocumentMouseListeners(): void {
    if (this.mouseMoveListener) {
      this.mouseMoveListener();
      this.mouseMoveListener = undefined;
    }
    if (this.mouseUpListener) {
      this.mouseUpListener();
      this.mouseUpListener = undefined;
    }
  }

  private static getClientY(e: TouchEvent | MouseEvent): number {
    if ('touches' in e && e.touches.length > 0) {
      return e.touches[0].clientY;
    }
    if ('clientY' in e) {
      return e.clientY;
    }
    return 0;
  }

  private isHandleTarget(target: EventTarget | null): boolean {
    return !!(target as HTMLElement | null)?.closest(this.handleSelector);
  }

  private handleTouchStart(e: TouchEvent): void {
    if (e.touches.length !== 1) {
      this.resetGesture();
      return;
    }

    if (!this.isHandleTarget(e.target as HTMLElement)) {
      this.resetGesture();
      return;
    }

    this.lastPointerType = 'touch';
    this.canDragFromHandle = true;
    this.pointerStartY = BottomSheetGestureDirective.getClientY(e);
    this.pointerCurrentY = this.pointerStartY;
    this.isDragging.set(false);
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.canDragFromHandle) return;

    this.pointerCurrentY = BottomSheetGestureDirective.getClientY(e);
    this.applyPointerMove(e);
  }

  private handleTouchEnd(): void {
    this.lastTouchEndTime = Date.now();
    this.handlePointerEnd();
  }

  private handleMouseDown(e: MouseEvent): void {
    if (!this.isHandleTarget(e.target as HTMLElement)) {
      this.resetGesture();
      return;
    }

    if (this.lastPointerType === 'touch' && Date.now() - this.lastTouchEndTime < SYNTHETIC_MOUSE_IGNORE_MS) {
      return;
    }

    e.preventDefault();
    this.lastPointerType = 'mouse';
    this.canDragFromHandle = true;
    this.pointerStartY = e.clientY;
    this.pointerCurrentY = this.pointerStartY;
    this.isDragging.set(false);

    this.mouseMoveListener = this.renderer.listen(document, 'mousemove', (moveEvent: MouseEvent) =>
      this.handleMouseMove(moveEvent)
    );
    this.mouseUpListener = this.renderer.listen(document, 'mouseup', () => this.handleMouseUp());
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.canDragFromHandle) return;

    this.pointerCurrentY = e.clientY;
    this.applyPointerMove(null);
  }

  private handleMouseUp(): void {
    this.handlePointerEnd();
    this.removeDocumentMouseListeners();
  }

  private applyPointerMove(touchEvent: TouchEvent | null): void {
    const diff = this.pointerCurrentY - this.pointerStartY;

    if (diff > DRAG_THRESHOLD_PX) {
      this.isDragging.set(true);

      if (touchEvent?.cancelable) {
        touchEvent.preventDefault();
      }

      const targetEl = this.targetElement() || this.elementRef.nativeElement;
      this.renderer.setStyle(targetEl, 'transform', `translateY(${diff}px)`);
      this.renderer.setStyle(targetEl, 'transition', 'none');

      const dialogEl = this.dialogElement();
      if (dialogEl) {
        const opacity = Math.max(0.3, 1 - diff / 400);
        this.renderer.setStyle(dialogEl, '--backdrop-opacity', opacity.toString(), RendererStyleFlags2.DashCase);
      }
    }
  }

  private handlePointerEnd(): void {
    if (!this.canDragFromHandle || !this.isDragging()) {
      this.resetGesture();
      return;
    }

    const diff = this.pointerCurrentY - this.pointerStartY;
    const targetEl = this.targetElement() || this.elementRef.nativeElement;
    const threshold = this.dismissThreshold();

    if (diff > threshold) {
      this.dismiss.emit();
    } else {
      this.renderer.setStyle(targetEl, 'transition', 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)');
      this.renderer.setStyle(targetEl, 'transform', 'translateY(0)');

      setTimeout(() => {
        this.renderer.removeStyle(targetEl, 'transition');
        this.renderer.removeStyle(targetEl, 'transform');
      }, 300);

      const dialogEl = this.dialogElement();
      if (dialogEl) {
        this.renderer.removeStyle(dialogEl, '--backdrop-opacity', RendererStyleFlags2.DashCase);
      }
    }

    this.resetGesture();
  }

  private resetGesture(): void {
    this.canDragFromHandle = false;
    this.isDragging.set(false);
    this.pointerStartY = 0;
    this.pointerCurrentY = 0;
  }
}
